import type { UserValue } from '@atlassian/jira-issue-field-assignee/src/common/types.tsx';
import type { ParentFieldOption } from '@atlassian/jira-issue-field-parent/src/common/types.tsx';
import type { Option as PriorityOption } from '@atlassian/jira-issue-field-priority/src/ui/edit/types.tsx';
import type { VersionOption } from '@atlassian/jira-issue-field-versions/src/common/types.tsx';
import type { LabelOption as Option } from '@atlassian/jira-issue-shared-types/src/common/types/labels-type.tsx';
import type {
	ASSIGNEE_TYPE,
	LABELS_TYPE,
	DUE_DATE_TYPE,
	PRIORITY_TYPE,
	FIX_VERSIONS_TYPE,
	COLOR_CF_TYPE,
	PARENT_TYPE,
	STATUS_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { BulkOperationsTypes } from '../use-bulk-operations/constants.tsx';

type ConfigField = {
	gqlType: string;
	fieldId: string;
	fieldName: string;
	fieldType: string;
};

export type FieldConfigs = {
	[key: string]: ConfigField;
};

export type FormData = {
	[key: string]: unknown;
};

export type MultiActionsType = 'add' | 'set' | 'remove';

type FormAssigneeData = {
	[ASSIGNEE_TYPE]?: UserValue;
};
type FormDuedateData = {
	[DUE_DATE_TYPE]?: string | null;
};
type FormPriorityData = {
	[PRIORITY_TYPE]?: PriorityOption;
};

type FormIssueColorData = {
	[COLOR_CF_TYPE]?: string;
};

type FormLabelsData = {
	[LABELS_TYPE]?: Option[] | Option;
};

type FormFixVersionsData = {
	[FIX_VERSIONS_TYPE]?: VersionOption[];
};

type FormParentData = {
	[PARENT_TYPE]?: ParentFieldOption | null;
};

type FormStatusData = {
	[STATUS_TYPE]?: {
		statusId: string;
		statusCategoryId: string;
	};
};

type FormSharedData = FormData &
	FormAssigneeData &
	FormDuedateData &
	FormPriorityData &
	FormParentData &
	FormStatusData;

export type FormSubmitData = FormSharedData & FormLabelsData & FormFixVersionsData;

type FormCallbackLabelsData = {
	[LABELS_TYPE]?: {
		action: MultiActionsType;
		value: Option[] | Option;
	};
};

type FormCallbackFixVersionsData = {
	[FIX_VERSIONS_TYPE]?: {
		value: VersionOption[];
		action: MultiActionsType;
	};
};
export type IssuesCallbackData = FormSharedData &
	FormIssueColorData &
	FormCallbackFixVersionsData &
	FormCallbackLabelsData;

export type IssueUpdateCallbackPayload = {
	issueIds: string[];
	data: IssuesCallbackData;
	// Make required when cleaning up bulk_operations_in_nin_experiment
	bulkOperationType?: BulkOperationsTypes;
};

export type IssuesUpdatePayload = {
	issueIds: number[];
	data: IssuesCallbackData;
	bulkOperationType?: BulkOperationsTypes;
};

export type IssueUpdateFlagData = {
	customDescription?: string;
};

export type FormSubmitStateCallbacks = {
	onUpdateStart?: (data: IssuesUpdatePayload[]) => void;
	onUpdateError?: (issueIds: number[]) => void;
	onUpdateSuccess?: (issueIds: number[]) => void;
	onLegacyBulkEditRequest?: (issueIds: number[]) => void;
};

type FormSubmitStateExternalProps = {
	callbacks: FormSubmitStateCallbacks;
	issueIds: number[];
};

export const SubmitFlagStatus = {
	ProgressFlag: 'ProgressFlag',
	SuccessFlag: 'SuccessFlag',
	ErrorFlag: 'ErrorFlag',
	PartialErrorFlag: 'PartialErrorFlag',
	NoFlag: 'NoFlag',
} as const;

export type SubmitFlagStatus = (typeof SubmitFlagStatus)[keyof typeof SubmitFlagStatus];

export type SubmitInProgressData = {
	taskId: string;
	progress?: number;
};

export type SubmitErrorData = {
	issueKeys: string[];
	totalCount: number;
	failedCount: number;
};

type FormSubmitFlagState =
	| {
			flagStatus: typeof SubmitFlagStatus.ProgressFlag | typeof SubmitFlagStatus.NoFlag;
			flagData?: IssueUpdateFlagData;
			data: SubmitInProgressData;
	  }
	| {
			flagStatus: typeof SubmitFlagStatus.SuccessFlag;
			flagData?: IssueUpdateFlagData;
			data: {
				numberOfIssuesUpdated: number;
			};
	  }
	| {
			flagStatus: typeof SubmitFlagStatus.ErrorFlag;
			flagData?: IssueUpdateFlagData;
			data: { issueIds?: string[] };
	  }
	| {
			flagStatus: typeof SubmitFlagStatus.PartialErrorFlag;
			flagData?: IssueUpdateFlagData;
			data: SubmitErrorData;
	  }
	| {
			flagStatus: typeof SubmitFlagStatus.NoFlag;
			flagData: undefined;
			data: undefined;
	  };

export type FormSubmitStateContainerProps = FormSubmitStateCallbacks & {
	selectedIssueIds: number[];
};
export type FormSubmitState = FormSubmitFlagState &
	FormSubmitStateExternalProps & {
		interactionSource?: string;
	};
